import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  LinearProgress,
  Divider,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Security,
  CheckCircle,
  Error as ErrorIcon
} from '@mui/icons-material';

const BulkApproversManagement = ({
  open,
  onClose,
  accounts,
  authenticatedFetch,
  API_ENDPOINT,
  onComplete
}) => {
  const [action, setAction] = useState('replace');
  const [userInput, setUserInput] = useState('');
  const [validatedUsers, setValidatedUsers] = useState([]);
  const [validating, setValidating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setAction('replace');
      setUserInput('');
      setValidatedUsers([]);
      setValidating(false);
      setUpdating(false);
      setProgress(0);
      setResults(null);
    }
  }, [open]);

  const parseUserIds = (input) => {
    return input
      .split(/[\s,;]+/)
      .map(id => id.trim())
      .filter(id => id.length > 0);
  };

  const handleValidate = async () => {
    const usernames = parseUserIds(userInput);
    if (usernames.length === 0) return;

    setValidating(true);
    setValidatedUsers([]);
    try {
      const response = await authenticatedFetch(
        `${API_ENDPOINT}/accounts/${accounts[0]?.id}/approvers/validate`,
        {
          method: 'POST',
          body: JSON.stringify({ usernames })
        }
      );
      const data = await response.json();
      if (data.validated_users) {
        setValidatedUsers(data.validated_users);
      } else {
        setSnackbar({ open: true, message: 'Validation failed: unexpected response', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: `Validation error: ${err.message}`, severity: 'error' });
    } finally {
      setValidating(false);
    }
  };

  const handleUpdate = async () => {
    const validUsernames = validatedUsers
      .filter(u => u.valid)
      .map(u => u.user_name);

    if (validUsernames.length === 0) return;

    setUpdating(true);
    setProgress(0);
    setResults(null);

    try {
      const response = await authenticatedFetch(
        `${API_ENDPOINT}/accounts/bulk/approvers`,
        {
          method: 'PUT',
          body: JSON.stringify({
            account_ids: accounts.map(a => a.id),
            approvers: validUsernames,
            action
          })
        }
      );
      const data = await response.json();

      setResults(data);
      setProgress(data.total || accounts.length);

      if (data.success) {
        const msg = data.failed > 0
          ? `Updated ${data.succeeded} of ${data.total} accounts (${data.failed} failed)`
          : `Successfully updated all ${data.total} accounts`;
        setSnackbar({ open: true, message: msg, severity: data.failed > 0 ? 'warning' : 'success' });
        if (onComplete) onComplete();
      } else {
        setSnackbar({ open: true, message: data.error || 'Bulk update failed', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: `Update error: ${err.message}`, severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const hasValidUsers = validatedUsers.some(u => u.valid);
  const canUpdate = hasValidUsers && !updating && !validating;

  const handleClose = () => {
    if (updating) return; // Prevent close during update
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={updating}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.95rem', pb: 1 }}>
          <Security color="primary" fontSize="small" />
          Bulk Approvers Management
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 2 }}>
          {/* Warning Banner */}
          <Alert severity="warning" sx={{ mb: 2, '& .MuiAlert-message': { fontSize: '0.78rem' } }}>
            This will update approvers for <strong>{accounts.length}</strong> account{accounts.length !== 1 ? 's' : ''} across all access levels.
          </Alert>

          {/* Affected Accounts Preview */}
          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block', mb: 0.5 }}>
            Affected Accounts
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
            {accounts.slice(0, 10).map((account) => (
              <Chip
                key={account.id}
                label={account.name || account.id}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 24 }}
              />
            ))}
            {accounts.length > 10 && (
              <Chip
                label={`+${accounts.length - 10} more`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 24 }}
              />
            )}
          </Box>

          {/* Action Mode Dropdown */}
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel sx={{ fontSize: '0.78rem' }}>Action</InputLabel>
            <Select
              value={action}
              onChange={(e) => setAction(e.target.value)}
              label="Action"
              sx={{ fontSize: '0.78rem' }}
            >
              <MenuItem value="replace" sx={{ fontSize: '0.78rem' }}>Replace all approvers</MenuItem>
              <MenuItem value="add" sx={{ fontSize: '0.78rem' }}>Add to existing approvers</MenuItem>
              <MenuItem value="remove" sx={{ fontSize: '0.78rem' }}>Remove from existing approvers</MenuItem>
            </Select>
          </FormControl>

          {/* User ID Input */}
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            label="User IDs"
            placeholder="I70625, I21893, I70794"
            value={userInput}
            onChange={(e) => {
              setUserInput(e.target.value);
              setValidatedUsers([]); // Reset validation when input changes
              setResults(null);
            }}
            sx={{ mb: 1, '& .MuiInputBase-input': { fontSize: '0.78rem' } }}
          />

          {/* Validate Button */}
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleValidate}
              disabled={!userInput.trim() || validating || updating}
              sx={{ fontSize: '0.78rem' }}
            >
              {validating ? (
                <>
                  <CircularProgress size={14} sx={{ mr: 1 }} />
                  Validating...
                </>
              ) : (
                'Validate User IDs'
              )}
            </Button>
          </Box>

          {/* Validation Results */}
          {validatedUsers.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block', mb: 0.5 }}>
                Validation Results
              </Typography>
              {validatedUsers.map((user, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                  {user.valid ? (
                    <CheckCircle sx={{ fontSize: 14, color: 'success.main' }} />
                  ) : (
                    <ErrorIcon sx={{ fontSize: 14, color: 'error.main' }} />
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.78rem',
                      color: user.valid ? 'text.primary' : 'error.main'
                    }}
                  >
                    {user.user_name}
                    {user.valid && user.name ? ` → ${user.name}` : ''}
                    {!user.valid && user.error ? ` — ${user.error}` : !user.valid ? ' — User not found' : ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          <Divider sx={{ my: 1.5 }} />

          {/* Progress during update */}
          {updating && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress sx={{ mb: 1 }} />
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                Processing {accounts.length} accounts...
              </Typography>
            </Box>
          )}

          {/* Results Summary */}
          {results && (
            <Box sx={{ mb: 1 }}>
              {results.success ? (
                <Alert
                  severity={results.failed > 0 ? 'warning' : 'success'}
                  sx={{ mb: 1, '& .MuiAlert-message': { fontSize: '0.78rem' } }}
                >
                  {results.succeeded} of {results.total} accounts updated successfully.
                  {results.failed > 0 && ` ${results.failed} failed.`}
                </Alert>
              ) : (
                <Alert severity="error" sx={{ mb: 1, '& .MuiAlert-message': { fontSize: '0.78rem' } }}>
                  {results.error || 'Bulk update failed'}
                </Alert>
              )}

              {/* Failed accounts detail */}
              {results.results && results.results.filter(r => !r.success).length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'error.main', display: 'block', mb: 0.5 }}>
                    Failed Accounts:
                  </Typography>
                  {results.results.filter(r => !r.success).map((r, idx) => (
                    <Typography key={idx} variant="body2" sx={{ fontSize: '0.7rem', color: 'text.secondary', ml: 1 }}>
                      • {r.account_id}: {r.error || 'Unknown error'}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button
            onClick={handleClose}
            disabled={updating}
            size="small"
            sx={{ fontSize: '0.78rem' }}
          >
            {results ? 'Close' : 'Cancel'}
          </Button>
          {!results && (
            <Button
              variant="contained"
              onClick={handleUpdate}
              disabled={!canUpdate}
              size="small"
              sx={{ fontSize: '0.78rem' }}
            >
              {updating ? 'Updating...' : `Update All (${accounts.length})`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%', fontSize: '0.78rem' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BulkApproversManagement;
